import { type ReportConfigData, type ReportConfigId } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { reportConfigListKey } from '../../keys'

export const useReportConfigCache = (): {
  updateItem: (reportConfig: ReportConfigData) => void
  addItem: (reportConfig: ReportConfigData) => void
  removeItem: (reportConfigId: ReportConfigId) => void
} => {
  const queryClient = useQueryClient()

  const setUnfiltered = (updater: (prev: ReportConfigData[] | undefined) => ReportConfigData[]): void => {
    queryClient.setQueriesData<ReportConfigData[]>({ queryKey: reportConfigListKey(), exact: true }, updater)
  }

  const setFiltered = (
    configType: ReportConfigData['configType'],
    updater: (prev: ReportConfigData[] | undefined) => ReportConfigData[] | undefined
  ): void => {
    queryClient.setQueriesData<ReportConfigData[]>({ queryKey: reportConfigListKey(configType) }, updater)
  }

  const removeFromFiltered = (configType: ReportConfigData['configType'], id: ReportConfigId): void => {
    setFiltered(configType, (prev) => {
      if (!prev) return prev
      return prev.filter((c) => c.reportConfigId !== id)
    })
  }

  return {
    updateItem: (reportConfig: ReportConfigData) => {
      setUnfiltered((prev) => {
        if (!prev) return [reportConfig]
        return prev.map((c) => (c.reportConfigId === reportConfig.reportConfigId ? reportConfig : c))
      })
      setFiltered(reportConfig.configType, (prev) => {
        if (!prev) return [reportConfig]
        const exists = prev.some((c) => c.reportConfigId === reportConfig.reportConfigId)
        if (exists) return prev.map((c) => (c.reportConfigId === reportConfig.reportConfigId ? reportConfig : c))
        return [...prev, reportConfig]
      })
      const otherType = reportConfig.configType === 'saved' ? 'scheduled' : 'saved'
      removeFromFiltered(otherType, reportConfig.reportConfigId)
    },
    addItem: (reportConfig: ReportConfigData) => {
      const append = (prev: ReportConfigData[] | undefined): ReportConfigData[] => {
        if (!prev) return [reportConfig]
        return [...prev, reportConfig]
      }
      setUnfiltered(append)
      setFiltered(reportConfig.configType, append)
    },
    removeItem: (reportConfigId: ReportConfigId) => {
      setUnfiltered((prev) => {
        if (!prev) return []
        return prev.filter((c) => c.reportConfigId !== reportConfigId)
      })
      removeFromFiltered('saved', reportConfigId)
      removeFromFiltered('scheduled', reportConfigId)
    },
  }
}
